import React from 'react';
import PropTypes from 'prop-types';
import { injectIntl } from 'react-intl';
import MakeHash from 'make-hash';
import classNames from 'classnames';
import Text from 'components/text/text';
import Cards from 'components/cards/cards';
import Card from 'components/cards/card';
import ProductSizeOverlay from 'pages/product-matches/_product-size-overlay';
import TransitionLink from 'components/transition-link/transition-link';
import messages from './messages';

class LastSessionModule extends React.Component {
  constructor(props) {
    super(props);

    const { intl } = props;

    this.labels = {
      'recommendedLabel': intl.formatMessage(messages.recommended_label),
      'startingLabel': intl.formatMessage(messages.starting_label),
      'bikeSizeLabel': intl.formatMessage(messages.bike_size_label),
      'saddleHeightLabel': intl.formatMessage(messages.saddle_height_label),
      'saddleSizeLabel': intl.formatMessage(messages.saddle_size_label),
      'shoeSizeLabel': intl.formatMessage(messages.shoe_size_label),
      'millimeterSymbol': intl.formatMessage(messages.millimeter_symbol),
      'centimeterSymbol': intl.formatMessage(messages.centimeter_symbol)
    };
  }

  showSizeOverlay({ label, field, value, unit }) {
    const dialogView = <ProductSizeOverlay
      label={label}
      field={field}
      value={value}
      unit={unit}
      theme="light"
    />;

    this.props.toggleDialog(dialogView, true);
  }

  _renderCardFooter() {
    const { intl } = this.props;

    return (
      <div className="block block_split">
        <div className="block-item">
          <Text
            color="DarkGray"
            type="sm"
            weight="normal"
          >
            {intl.formatMessage(messages.last_session_match_label)}
          </Text>
        </div>

        <div className="block-item">
          <Text
            color="DarkGray"
            type="sm"
            weight="normal"
          >
            {intl.formatDate(this.props.sessionData.updated_at, { 'year': 'numeric', 'month': 'numeric', 'day': '2-digit' })}
          </Text>
        </div>

        <div className="block-item">
          <Text
            color="DarkGray"
            type="sm"
            weight="normal"
          >
            {`${this.props.sessionData.shop_name} ${this.props.sessionData.shop_city}`}
          </Text>
        </div>

        <div className="block-item">
          <Text
            color="DarkGray"
            type="sm"
            weight="normal"
          >
            {this.props.sessionData.employee_name}
          </Text>
        </div>
      </div>
    );
  }

  _renderDataSet({ label, descriptor, value, unit, field, key, centeredSet, narrowSet }) {
    const setClasses = classNames(
      'set',
      {
        'mix-set_centered': centeredSet,
        'mix-set_narrow': narrowSet,
      }
    );

    return (
      <Card
        hasInfo
        key={key}
        showOverlay={this.showSizeOverlay.bind(this, { label, field, value, unit })}
      >
        <div className={setClasses}>
          <div className="set-hd">
            <Text
              className="mix-txt_uppercase"
              color="DefaultGray"
              tag="h2"
              weight="normal"
            >
              {label}
            </Text>
          </div>
          <div className="set-bd set-bd_compact">
            <Text
              className="mix-txt_uppercase"
              color="Darkest"
              tag="span"
              type="lg"
              weight="light"
            >
              {descriptor}
            </Text>
          </div>
          <div className="set-bd">
            <Text
              color="Darkest"
              height="flat"
              tag="span"
              type="giant"
              weight="light"
              className="mix-txt_squeeze"
            >
              {value}<span className="txt txt_xl mix-txt_lowercase">{unit}</span>
            </Text>
          </div>
        </div>
      </Card>
    );
  }

  _renderEmptyState() {
    const { intl } = this.props;

    return(
      <Cards light>
        <Card>
          <div className="set mix-set_wide mix-set_centered mix-set_alignCenter">
            <div className="set-bd">
              <Text
                color="DarkGray"
                type="md"
                weight="light"
              >
                {intl.formatMessage(messages.empty_session)}
              </Text>
            </div>
            <div className="set-bd set-bd_separated">
              <TransitionLink
                animateOnClick
                to="/locations"
                className="btn btn_light btn_fixedWidthLg"
              >
                {intl.formatMessage(messages.find_retul_technology_label)}
              </TransitionLink>
            </div>
          </div>
        </Card>
      </Cards>
    );
  }

  _renderContent() {
    let cardData;
    const footerContent = this._renderCardFooter();

    switch(this.props.type) {
      case 'bike':
        cardData = [
          this._renderDataSet({
            'label': this.labels.recommendedLabel,
            'descriptor': this.labels.bikeSizeLabel,
            'value': this.props.sizingData.bikeSize,
            'unit': this.props.sizingData.bikeSizeMountain ? '' : this.labels.centimeterSymbol,
            'field': 'bike_size',
            'key': MakeHash('card-bike-size')
          }),
          this._renderDataSet({
            'label': this.labels.startingLabel,
            'descriptor': this.labels.saddleHeightLabel,
            'value': this.props.sizingData.saddleHeight,
            'unit': this.labels.millimeterSymbol,
            'field': 'saddle_height',
            'key': MakeHash('card-saddle-height')
          })
        ];
        break;
      case 'shoe':
        cardData = this._renderDataSet({
          'label': this.labels.recommendedLabel,
          'descriptor': this.labels.shoeSizeLabel,
          'value': this.props.sizingData.shoeSize,
          'unit': this.props.sizingData.shoeWidth,
          'field': 'shoe_size',
          'centeredSet': true,
          'narrowSet': true,
        });
        break;
      case 'saddle':
        cardData = this._renderDataSet({
          'label': this.labels.recommendedLabel,
          'descriptor': this.labels.saddleSizeLabel,
          'value': this.props.sizingData.saddleSize,
          'unit': this.labels.millimeterSymbol,
          'field': 'saddle_size',
          'centeredSet': true,
          'narrowSet': true,
        });
        break;
    }

    return(
      <Cards light hasInfo hasFooter footerContent={footerContent}>
        {cardData}
      </Cards>
    );
  }

  render() {
    if (this.props.sessionData.empty) {
      return this._renderEmptyState();
    }
    else {
      return this._renderContent();
    }
  }
}

const sessionDataShape = PropTypes.shape({
  'employee_name': PropTypes.string,
  'empty': PropTypes.bool.isRequired,
  'shop_city': PropTypes.string,
  'shop_name': PropTypes.string,
  'updated_at': PropTypes.string
});

const sizingDataShape = PropTypes.shape({
  'bikeSize': PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  'bikeSizeMount': PropTypes.bool,
  'saddleHeight': PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  'saddleSize': PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  'shoeSize': PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  'shoeWidth': PropTypes.string
});

LastSessionModule.propTypes = {
  'sessionData': sessionDataShape.isRequired,
  'sizingData': sizingDataShape.isRequired,
  'toggleDialog': PropTypes.func,
  'type': PropTypes.oneOf(['bike', 'saddle', 'shoe']).isRequired
};

export default injectIntl(LastSessionModule);



// WEBPACK FOOTER //
// ./src/client/pages/product-matches/_last-session-module.jsx